import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { AdminAreaView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";
import type { AreasListEnvelope } from "./useAreasQuery";

// The admin areas list caps `limit` at 100 server-side (see
// admin-area.controller.ts / AreaListQueryDto). Walking pages at that size
// is what lets this hook fetch "all areas in a governorate" without a
// separate unpaginated backend mode.
const HINT_PAGE_LIMIT = 100;

async function fetchAllAreasForGovernorate(
  governorateId: string,
): Promise<AdminAreaView[]> {
  const all: AdminAreaView[] = [];
  let page = 1;
  for (;;) {
    const path = `${endpoints.admin.areas}?governorateId=${encodeURIComponent(
      governorateId,
    )}&page=${page}&limit=${HINT_PAGE_LIMIT}`;
    const envelope = await apiJsonClient<AreasListEnvelope>(path);
    all.push(...envelope.items);
    if (envelope.items.length === 0 || all.length >= envelope.total) {
      break;
    }
    page += 1;
  }
  return all;
}

/**
 * Deliberately unpaginated (per governorate) fetch used ONLY by the area
 * suggestions "looks like a duplicate" hint
 * (modules/areas/lib/duplicate-hint.ts). That check only ever compares a
 * suggestion against areas in ITS OWN governorate (it skips areas from any
 * other governorate), so instead of fetching the whole ~940KB catalog via
 * `useAreasQuery`, this walks every page for just the governorates that
 * actually appear in the current suggestion list — a handful of
 * governorates at most, not all 27.
 *
 * Do NOT reuse this for the areas list page or any other list UI — it is
 * not real pagination, it is "give me everything for these specific
 * governorates" and exists only to keep the duplicate hint accurate now
 * that the areas list itself is paginated.
 */
export function useAreasForDuplicateHintQuery(governorateIds: string[]) {
  const ids = useMemo(
    () => [...new Set(governorateIds)].sort(),
    [governorateIds],
  );

  const results = useQueries({
    queries: ids.map((governorateId) => ({
      queryKey: queryKeys.admin.areasAllForGovernorate(governorateId),
      queryFn: () => fetchAllAreasForGovernorate(governorateId),
      staleTime: 60_000,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const data = useMemo(() => {
    if (results.some((r) => r.data === undefined)) {
      return undefined;
    }
    return results.flatMap((r) => r.data ?? []);
  }, [results]);

  return { data, isLoading };
}
